/**
 * V1e+ · CredentialStatsPanel — vue stats agrégées de la queue modération.
 *
 * Affiche en haut de la page admin une bande de 4 stats cards (par status)
 * + une carte secondaire "Performance" avec le temps moyen de modération
 * et les top 5 modérateurs sur les N derniers jours.
 *
 * Apport opérationnel V1e+ :
 *   - Détection rapide d'un backlog ("12 en attente" rouge si > seuil)
 *   - Mesure SLA modération (cible <48h V1e)
 *   - Visibilité de la charge entre admins (load balancing manuel)
 *
 * Pas de seuil dur côté front pour l'instant (toutes les couleurs sont
 * indicatives). Si on veut alerter sur backlog>20 ou avg>72h, on ajoutera
 * un système d'alerting V1f.
 */

import { useQuery } from '@apollo/client';
import {
  Box,
  Paper,
  Stack,
  Typography,
  Alert,
  LinearProgress,
} from '@mui/material';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import TimerIcon from '@mui/icons-material/Timer';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';

import { GET_CREDENTIAL_MODERATION_STATS } from './credentials.queries';
import type { CredentialModerationStats } from './credentials.types';

interface StatCardProps {
  label: string;
  value: string | number;
  color: string;
  icon: React.ReactNode;
  subline?: string;
}

function StatCard({ label, value, color, icon, subline }: StatCardProps) {
  return (
    <Paper
      sx={{
        p: 2,
        flex: 1,
        minWidth: 140,
        borderLeft: `4px solid ${color}`,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Box sx={{ color }}>{icon}</Box>
        <Box>
          <Typography variant="overline" sx={{ color: 'text.secondary' }}>
            {label}
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {value}
          </Typography>
          {subline && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {subline}
            </Typography>
          )}
        </Box>
      </Stack>
    </Paper>
  );
}

/**
 * Helper formatter pour avgModerationTimeHours :
 *   - null      → "—"
 *   - < 1h      → "Xmin"
 *   - < 24h     → "Xh"
 *   - >= 24h    → "Xj"
 */
function formatAvgTime(hours: number | null): string {
  if (hours == null) return '—';
  if (hours < 1) return `${Math.round(hours * 60)}min`;
  if (hours < 24) return `${hours.toFixed(1)}h`;
  return `${(hours / 24).toFixed(1)}j`;
}

interface CredentialStatsPanelProps {
  /** Fenêtre temporelle pour avgModerationTime + topReviewers (default 30j). */
  windowDays?: number;
}

export function CredentialStatsPanel({
  windowDays = 30,
}: CredentialStatsPanelProps) {
  const { data, loading, error } = useQuery<{
    credentialModerationStats: CredentialModerationStats;
  }>(GET_CREDENTIAL_MODERATION_STATS, {
    variables: { windowDays },
    fetchPolicy: 'cache-and-network',
  });

  if (loading && !data) {
    return (
      <Box sx={{ mb: 3 }}>
        <LinearProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="warning" sx={{ mb: 3 }} data-testid="credential-stats-error">
        Stats indisponibles : {error.message}
      </Alert>
    );
  }

  const stats = data?.credentialModerationStats;
  if (!stats) return null;

  return (
    <Box sx={{ mb: 3 }} data-testid="credential-stats-panel">
      {/* Counts par status */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        sx={{ mb: 2 }}
      >
        <StatCard
          label="En attente"
          value={stats.countPending}
          color="#F59E0B"
          icon={<HourglassEmptyIcon />}
        />
        <StatCard
          label="En cours d'examen"
          value={stats.countUnderReview}
          color="#3B82F6"
          icon={<VisibilityIcon />}
        />
        <StatCard
          label="Approuvées"
          value={stats.countApproved}
          color="#10B981"
          icon={<CheckCircleIcon />}
        />
        <StatCard
          label="Refusées"
          value={stats.countRejected}
          color="#EF4444"
          icon={<CancelIcon />}
        />
      </Stack>

      {/* Performance — temps moyen + top reviewers */}
      <Paper sx={{ p: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
          {/* Temps moyen */}
          <Box sx={{ minWidth: 200 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
              <TimerIcon sx={{ color: 'text.secondary' }} />
              <Typography variant="overline" sx={{ color: 'text.secondary' }}>
                Temps moyen modération
              </Typography>
            </Stack>
            <Typography
              variant="h4"
              sx={{ fontWeight: 700 }}
              data-testid="credential-stats-avg-time"
            >
              {formatAvgTime(stats.avgModerationTimeHours)}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Sur {stats.windowDays} derniers jours
            </Typography>
          </Box>

          {/* Top reviewers */}
          <Box sx={{ flex: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <EmojiEventsIcon sx={{ color: 'text.secondary' }} />
              <Typography variant="overline" sx={{ color: 'text.secondary' }}>
                Top modérateurs ({stats.windowDays}j)
              </Typography>
            </Stack>
            {stats.topReviewers.length === 0 ? (
              <Typography
                variant="body2"
                sx={{ color: 'text.secondary', fontStyle: 'italic' }}
                data-testid="credential-stats-no-reviewers"
              >
                Aucune modération dans la fenêtre.
              </Typography>
            ) : (
              <Stack
                direction="row"
                spacing={2}
                flexWrap="wrap"
                useFlexGap
                data-testid="credential-stats-top-reviewers"
              >
                {stats.topReviewers.map((r, idx) => (
                  <Box
                    key={r.userId}
                    data-testid={`credential-stats-reviewer-${idx}`}
                    sx={{
                      px: 1.5,
                      py: 0.5,
                      bgcolor: idx === 0 ? '#FEF3C7' : '#F4F4F5',
                      border: idx === 0 ? '1px solid #F59E0B' : '1px solid #E5E7EB',
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                      {r.userId.slice(0, 8)}…
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, lineHeight: 1.2 }}
                    >
                      {r.reviewedCount}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            )}
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
}

// Exports pour testing helpers (utile en V1f si on ajoute des tests Vitest)
export const __test__ = {
  formatAvgTime,
};
